import React, { useState } from "react";
import { Polyline, Marker, CircleMarker, Popup } from "react-leaflet";
import { Icon } from "leaflet";
import type { LeafletMouseEvent } from "leaflet";
import { useRouteStore } from "../../store/useRouteStore";
import { useRoutePlayer } from "../../hooks/useRoutePlayer";
import { RouteProcessor } from "../../utils/routeUtils";
import { ROUTE_THRESHOLDS } from "../../utils/constants";
import type {
  ProcessedRoute,
  RouteTrip,
  RouteTripGap,
} from "../../types/route";
import { RouteGapPopup, RoutePointPopup } from "./RoutePointPopup";

interface RoutePathsProps {
  routes: ProcessedRoute[];
}

const GAP_COLORS: Record<string, string> = {
  time_gap: "#6366f1",
  long_stop: "#f59e0b",
  teleport: "#ef4444",
};

export const RoutePaths: React.FC<RoutePathsProps> = ({ routes }) => {
  const {
    selectedRoute,
    playerState,
    showStops,
    showSegments,
    showGpsPoints,
    colorBySpeed,
    selectedPointInfo,
    setSelectedPointInfo,
    seekToFrame,
  } = useRouteStore();

  const { getCurrentPosition } = useRoutePlayer();
  const [selectedGap, setSelectedGap] = useState<RouteTripGap | null>(null);

  const getSpeedColor = (speed: number = 0) => {
    if (speed < 5) return "#ef4444"; // red
    if (speed < 15) return "#f59e0b"; // yellow
    if (speed < 25) return "#22c55e"; // green
    return "#3b82f6"; // blue
  };

  const getRouteColor = (route: ProcessedRoute, isSelected: boolean) => {
    if (isSelected) return "#3b82f6";

    // Use different colors for multiple routes
    const colors = ["#8b5cf6", "#06b6d4", "#10b981", "#f59e0b", "#ef4444"];
    const index = routes.findIndex((r) => r.bike_id === route.bike_id);
    return colors[index % colors.length];
  };

  const isRouteFocused = (route: ProcessedRoute) =>
    !selectedRoute || selectedRoute.bike_id === route.bike_id;

  const handlePointClick = (
    route: ProcessedRoute,
    tripIndex: number,
    pointIndex: number,
    point: RouteTrip["coordinates"][number],
  ) => {
    setSelectedGap(null);
    setSelectedPointInfo({
      point,
      tripIndex,
      pointIndex,
      totalTrips: route.total_trips,
    });
  };

  const handlePolylineClick = (
    route: ProcessedRoute,
    trip: RouteTrip,
    event: LeafletMouseEvent,
  ) => {
    const { lat, lng } = event.latlng;
    const { point, index } = RouteProcessor.findNearestPoint(
      trip.coordinates,
      lat,
      lng,
    );
    handlePointClick(route, trip.trip_index, index, point);
  };

  const renderTripSolid = (route: ProcessedRoute, trip: RouteTrip) => {
    const focused = isRouteFocused(route);
    const isSelected = selectedRoute?.bike_id === route.bike_id;
    const baseColor = getRouteColor(route, isSelected);
    const opacity = focused ? (isSelected ? 0.85 : 0.5) : 0.35;
    const weight = isSelected ? 4 : 3;
    const useMarkers =
      showGpsPoints &&
      isSelected &&
      trip.coordinates.length <= ROUTE_THRESHOLDS.GPS_POINTS_MARKER_THRESHOLD;

    const polylinePositions = trip.coordinates.map(
      (coord) => [coord.latitude, coord.longitude] as [number, number],
    );

    if (colorBySpeed && isSelected) {
      return (
        <>
          {trip.coordinates.map((point, index) => {
            if (index === 0) return null;
            const prevPoint = trip.coordinates[index - 1];
            return (
              <Polyline
                key={`${route.bike_id}-trip-${trip.trip_index}-seg-${index}`}
                positions={[
                  [prevPoint.latitude, prevPoint.longitude],
                  [point.latitude, point.longitude],
                ]}
                pathOptions={{
                  color: getSpeedColor(point.speed),
                  weight,
                  opacity,
                }}
                eventHandlers={
                  !useMarkers
                    ? {
                        click: (e) => handlePolylineClick(route, trip, e),
                      }
                    : undefined
                }
              />
            );
          })}
        </>
      );
    }

    return (
      <Polyline
        key={`${route.bike_id}-trip-${trip.trip_index}`}
        positions={polylinePositions}
        pathOptions={{ color: baseColor, weight, opacity }}
        eventHandlers={
          !useMarkers && isSelected && showGpsPoints
            ? { click: (e) => handlePolylineClick(route, trip, e) }
            : undefined
        }
      />
    );
  };

  const renderTripPoints = (route: ProcessedRoute, trip: RouteTrip) => {
    if (
      !showGpsPoints ||
      selectedRoute?.bike_id !== route.bike_id ||
      trip.coordinates.length > ROUTE_THRESHOLDS.GPS_POINTS_MARKER_THRESHOLD
    ) {
      return null;
    }

    return trip.coordinates.map((point, pointIndex) => {
      const isSelectedPoint =
        selectedPointInfo?.point.timestamp === point.timestamp &&
        selectedPointInfo.tripIndex === trip.trip_index &&
        selectedPointInfo.pointIndex === pointIndex;

      return (
        <CircleMarker
          key={`${route.bike_id}-trip-${trip.trip_index}-pt-${pointIndex}`}
          center={[point.latitude, point.longitude]}
          radius={isSelectedPoint ? 6 : 4}
          pathOptions={{
            color: isSelectedPoint ? "#2563eb" : "#64748b",
            fillColor: isSelectedPoint ? "#dbeafe" : "#ffffff",
            fillOpacity: 0.9,
            weight: isSelectedPoint ? 2 : 1,
          }}
          eventHandlers={{
            click: () =>
              handlePointClick(route, trip.trip_index, pointIndex, point),
          }}
        >
          {isSelectedPoint && (
            <Popup>
              <RoutePointPopup
                point={point}
                tripIndex={trip.trip_index}
                pointIndex={pointIndex}
                totalTrips={route.total_trips}
                onJumpToPoint={() => {
                  const frameIndex = RouteProcessor.findFrameIndexForPoint(
                    route,
                    trip.trip_index,
                    pointIndex,
                  );
                  if (frameIndex >= 0) seekToFrame(frameIndex);
                }}
              />
            </Popup>
          )}
        </CircleMarker>
      );
    });
  };

  const renderGapConnector = (route: ProcessedRoute, gap: RouteTripGap) => {
    if (!showSegments || !isRouteFocused(route)) return null;

    const color = GAP_COLORS[gap.reason] ?? "#94a3b8";
    const isSelected = selectedRoute?.bike_id === route.bike_id;
    const opacity = isSelected ? 0.75 : 0.35;

    return (
      <Polyline
        key={`${route.bike_id}-gap-${gap.gap_index}`}
        positions={[
          [gap.from_point.latitude, gap.from_point.longitude],
          [gap.to_point.latitude, gap.to_point.longitude],
        ]}
        pathOptions={{
          color,
          weight: 2,
          opacity,
          dashArray: "8 8",
        }}
        eventHandlers={{
          click: () => setSelectedGap(gap),
        }}
      />
    );
  };

  const renderStops = (route: ProcessedRoute) => {
    if (!showStops || !isRouteFocused(route)) return null;

    return route.stops.map((stop, index) => (
      <CircleMarker
        key={`${route.bike_id}-stop-${index}`}
        center={[stop.location.latitude, stop.location.longitude]}
        radius={8}
        pathOptions={{
          color: "#ef4444",
          fillColor: "#fee2e2",
          fillOpacity: 0.9,
          weight: 2,
        }}
      />
    ));
  };

  const renderRouteEndpoints = (route: ProcessedRoute) => {
    if (selectedRoute?.bike_id !== route.bike_id || route.trips.length === 0) {
      return null;
    }

    const firstTrip = route.trips[0];
    const lastTrip = route.trips[route.trips.length - 1];
    const startPoint = firstTrip.coordinates[0];
    const endPoint = lastTrip.coordinates[lastTrip.coordinates.length - 1];

    const startIcon = new Icon({
      iconUrl: `data:image/svg+xml;base64,${btoa(`
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" fill="#22c55e" stroke="white" stroke-width="2"/>
          <text x="12" y="16" text-anchor="middle" fill="white" font-size="12" font-weight="bold">S</text>
        </svg>
      `)}`,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
    });

    const endIcon = new Icon({
      iconUrl: `data:image/svg+xml;base64,${btoa(`
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" fill="#ef4444" stroke="white" stroke-width="2"/>
          <text x="12" y="16" text-anchor="middle" fill="white" font-size="12" font-weight="bold">E</text>
        </svg>
      `)}`,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
    });

    return (
      <>
        <Marker
          position={[startPoint.latitude, startPoint.longitude]}
          icon={startIcon}
        />
        <Marker
          position={[endPoint.latitude, endPoint.longitude]}
          icon={endIcon}
        />
      </>
    );
  };

  const renderCurrentPosition = () => {
    const currentPos = getCurrentPosition();
    if (!currentPos || (!playerState.isPlaying && !playerState.isPaused)) {
      return null;
    }

    const currentIcon = new Icon({
      iconUrl: `data:image/svg+xml;base64,${btoa(`
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="8" fill="${currentPos.isInTransit ? "#94a3b8" : "#3b82f6"}" stroke="white" stroke-width="2"/>
          <circle cx="12" cy="12" r="4" fill="white"/>
        </svg>
      `)}`,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
    });

    return (
      <Marker
        position={[currentPos.latitude, currentPos.longitude]}
        icon={currentIcon}
      />
    );
  };

  const renderSelectedPointPopup = () => {
    if (selectedGap) {
      const midLat =
        (selectedGap.from_point.latitude + selectedGap.to_point.latitude) / 2;
      const midLng =
        (selectedGap.from_point.longitude + selectedGap.to_point.longitude) / 2;

      return (
        <Popup
          position={[midLat, midLng]}
          eventHandlers={{ remove: () => setSelectedGap(null) }}
        >
          <RouteGapPopup gap={selectedGap} />
        </Popup>
      );
    }

    if (!selectedPointInfo || !selectedRoute) return null;

    return (
      <Popup
        position={[
          selectedPointInfo.point.latitude,
          selectedPointInfo.point.longitude,
        ]}
        eventHandlers={{ remove: () => setSelectedPointInfo(null) }}
      >
        <RoutePointPopup
          point={selectedPointInfo.point}
          tripIndex={selectedPointInfo.tripIndex}
          pointIndex={selectedPointInfo.pointIndex}
          totalTrips={selectedPointInfo.totalTrips}
          onJumpToPoint={() => {
            const frameIndex = RouteProcessor.findFrameIndexForPoint(
              selectedRoute,
              selectedPointInfo.tripIndex,
              selectedPointInfo.pointIndex,
            );
            if (frameIndex >= 0) seekToFrame(frameIndex);
          }}
        />
      </Popup>
    );
  };

  return (
    <>
      {/* Render all routes */}
      {routes.map((route) => (
        <React.Fragment key={route.bike_id}>
          {route.trips.map((trip) => (
            <React.Fragment key={`${route.bike_id}-trip-${trip.trip_index}`}>
              {renderTripSolid(route, trip)}
              {renderTripPoints(route, trip)}
            </React.Fragment>
          ))}
          {renderStops(route)}
          {route.trip_gaps.map((gap) => renderGapConnector(route, gap))}
          {renderRouteEndpoints(route)}
        </React.Fragment>
      ))}

      {/* Current position marker during playback */}
      {renderCurrentPosition()}
      {renderSelectedPointPopup()}
    </>
  );
};
